import { ChangeEvent, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
// Context
import { useFiltersContext } from 'components/common3/PageWithFiltersWrapper/PageWithFiltersContext';
// Helpers
import { useDebouncedSearchRefetch } from 'helpers/filters';
// Api
import { GET_STORES_NAMES_LIST } from 'api/store/queries';
// Types
import {
  UserRole,
  StoresOrderBy,
  SortDirection,
} from 'api/graphql-global-types';
import {
  GetStoresNamesList,
  GetStoresNamesListVariables,
  GetStoresNamesList_stores_entities,
} from 'api/store/types/GetStoresNamesList';
// Ui
import InfiniteScrollLoader from 'ui3/InfiniteScrollLoader/InfiniteScrollLoader';
// Components
import CheckboxFilter from '../CheckboxFilter/CheckboxFilter';
// Styles
import styles from './AthletesFilter.module.scss';

const PER_PAGE = 8;

const AthletesFilter = () => {
  const router = useRouter();
  const [searchTerm, setSearchTerm] = useState(
    typeof router.query.athleteSearch === 'string'
      ? router.query.athleteSearch
      : ''
  );

  const {
    filters,
    storesListFilter,
    setFilters,
    searchKey,
    search,
  } = useFiltersContext();

  const inputVariables = useMemo(
    () => ({
      orderBy: StoresOrderBy.storeName,
      direction: SortDirection.ASC,
      limit: PER_PAGE,
      offset: 0,
      ...(storesListFilter && {
        [storesListFilter]: true,
      }),
    }),
    [storesListFilter]
  );

  const { data, fetchMore, refetch } = useQuery<
    GetStoresNamesList,
    GetStoresNamesListVariables
  >(GET_STORES_NAMES_LIST, {
    variables: {
      storeRoles: [UserRole.Athlete],
      input: {
        ...inputVariables,
      },
    },
    fetchPolicy: 'cache-first',
  });

  useDebouncedSearchRefetch({
    searchTerm,
    refetch,
    inputVariables,
  });

  const onCheckBoxChange = ({
    target: { id },
  }: ChangeEvent<HTMLInputElement>) => {
    const athleteIds: string[] = filters?.athleteIds?.includes(id)
      ? filters?.athleteIds?.filter((athleteId) => athleteId !== id) || []
      : [...(filters?.athleteIds || []), id];

    router.replace(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          athleteSearch: searchTerm || undefined,
          athleteIds: athleteIds.length > 0 ? athleteIds.join(',') : undefined,
        },
      },
      undefined,
      { shallow: true, scroll: false }
    );

    setFilters({ ...filters, athleteIds, athlete: [] });
  };

  const fetchMoreData = () => {
    const prevOffset = data?.stores?.offset || 0;
    const offset = prevOffset ? prevOffset + PER_PAGE : PER_PAGE;

    fetchMore({
      variables: {
        input: {
          ...inputVariables,
          offset,
          searchTerm: searchTerm.trim() || undefined,
        },
      },
    });
  };

  const handleSearchInputChange = (value: string) => {
    setSearchTerm(value);
    router.replace(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          athleteSearch: value || undefined,
        },
      },
      undefined,
      { shallow: true, scroll: false }
    );
  };

  const athletes = data?.stores?.entities || [];
  const total = data?.stores?.total;
  const hasMore = data ? athletes.length !== total : false;
  const infiniteScrollHeight = !athletes?.length
    ? 48
    : athletes.length >= 6
    ? 240
    : athletes.length * 48;

  const getIfChecked = (
    athlete: GetStoresNamesList_stores_entities
  ): boolean => {
    let isChecked =
      Boolean(filters?.athleteIds?.includes(athlete?.id)) ||
      filters?.athlete?.[0] === athlete.slug;

    if (
      searchKey === 'storeName' &&
      athlete?.storeDetails?.storeName === search?.[searchKey]
    ) {
      isChecked = true;
    }

    return isChecked;
  };

  const getLabel = (athlete: GetStoresNamesList_stores_entities) => {
    return athlete?.storeDetails?.storeName &&
      athlete?.storeDetails?.storeName.trim() !== ''
      ? athlete?.storeDetails?.storeName
      : `${athlete?.firstName} ${athlete?.lastName}`;
  };

  const isFilterOpen = Boolean(
    filters?.athleteIds?.length || searchTerm.trim()
  );

  return (
    <CheckboxFilter<GetStoresNamesList_stores_entities>
      title="Athletes"
      options={athletes}
      onChange={onCheckBoxChange}
      getOptionValue={(athlete) => athlete.id}
      getOptionLabel={(athlete) => getLabel(athlete)}
      getIfChecked={(athlete) => getIfChecked(athlete)}
      selectedValues={filters?.athleteIds || []}
      isOpen={isFilterOpen}
      searchInputValue={searchTerm}
      onSearchInputChange={handleSearchInputChange}
      infiniteScroll={{
        className: styles.athletesFilterWrapper,
        dataLength: athletes.length,
        next: fetchMoreData,
        hasMore,
        height: infiniteScrollHeight,
        loader: <InfiniteScrollLoader size="small" />,
      }}
    />
  );
};

export default AthletesFilter;
